import { useCallback, useEffect, useState } from 'react'
import SendBird, {
  AdminMessage,
  GroupChannel,
  GroupChannelListQuery,
  SendBirdError,
  UserMessage,
} from 'sendbird'

import { isInWhitelist } from 'main/util/error'
import { logError } from 'main/util/paymentMethod/common'

import useSendBirdInstance from './useSendBirdInstance'

const CHANNEL_HANDLER_ID = 'channelHandler'

export default function useSendbird(
  sendBirdAppId: string,
  userId: number | null,
  accessToken: string | null,
) {
  const [status, setStatus] = useState<string>('none')
  const [channels, setChannels] = useState<GroupChannel[]>([])
  const [lastUpdated, setLastUpdated] = useState<number>(Date.now)
  const [unreadMessageCount, setUnreadMessageCount] = useState<number>(0)

  const sendBirdInstance = useSendBirdInstance(sendBirdAppId)

  const generateFallbackChannelName = useCallback(
    (channel: GroupChannel) =>
      channel.members
        .filter(item => userId && item.userId !== userId.toString())
        .map(item => item.nickname)
        .join(', '),
    [userId],
  )

  const isOwnMessage = useCallback(
    (message: UserMessage) => {
      if (message && userId) {
        return message.sender ? message.sender.userId === userId.toString() : false
      } else {
        return false
      }
    },
    [userId],
  )

  const fetchUnreadMessageCount = (): Promise<number> => {
    return new Promise((resolve, reject) => {
      if (!sendBirdInstance) return resolve(0)

      sendBirdInstance.getTotalUnreadMessageCount((count, error) => {
        if (error) reject(error)

        resolve(count)
      })
    })
  }

  const fetchGroupChannels = (
    order: GroupChannelListQuery['order'],
    limit: GroupChannelListQuery['limit'],
  ): Promise<GroupChannel[]> => {
    return new Promise((resolve, reject) => {
      if (!sendBirdInstance) return resolve([])

      const query = sendBirdInstance.GroupChannel.createMyGroupChannelListQuery()
      query.includeEmpty = true
      query.order = order
      query.limit = limit

      query.next((groupChannels, error) => {
        if (error) {
          return reject(error)
        } else if (!groupChannels || groupChannels.length === 0) {
          return resolve([])
        } else {
          return resolve(groupChannels)
        }
      })
    })
  }

  const fetchLatestGroupChannelWithUnreadMessages = (): Promise<GroupChannel | undefined> => {
    return new Promise((resolve, reject) => {
      if (!sendBirdInstance) return resolve(undefined)

      const query = sendBirdInstance.GroupChannel.createMyGroupChannelListQuery()
      query.order = 'latest_last_message'
      query.unreadChannelFilter = 'unread_message'

      query.next((groupChannels, error) => {
        if (error) {
          return reject(error)
        } else if (!groupChannels || groupChannels.length === 0) {
          return resolve(undefined)
        } else {
          return resolve(groupChannels[0])
        }
      })
    })
  }

  const isSendBirdError = (error: any): error is SendBirdError => {
    return error && error.message && error.code
  }

  const updateUnreadMessageCount = () => {
    fetchUnreadMessageCount().then(setUnreadMessageCount)
  }

  useEffect(() => {
    if (!userId || !accessToken) {
      setStatus('none')
      if (sendBirdInstance) {
        sendBirdInstance.removeAllChannelHandlers()
        sendBirdInstance.disconnect()
      }
      return
    }

    if (sendBirdInstance && status === 'none') {
      const getSBChannelList = async () => {
        try {
          const latestLastMessageChannels = await fetchGroupChannels('latest_last_message', 100)
          const chronologicalMessageChannels = await fetchGroupChannels('chronological', 100)
          const uniqChannels = [
            ...latestLastMessageChannels,
            ...chronologicalMessageChannels,
          ].reduce((acc: { [key: string]: GroupChannel }, channel) => {
            acc[channel.url] = channel
            return acc
          }, {})

          const sortedChannels = Object.values(uniqChannels)
            .sort((a, b) => {
              const aTimestamp = a.lastMessage?.createdAt || a.createdAt
              const bTimestamp = b.lastMessage?.createdAt || b.createdAt
              return bTimestamp - aTimestamp
            })
            .slice(0, 100)

          updateUnreadMessageCount()
          setChannels(sortedChannels)
          setStatus('connected')
        } catch (err) {
          if (isSendBirdError(err)) {
            setStatus('error')
            logError(err.message, err)
          }
        }
      }

      const handleSBConnectCallback = async (_: SendBird.User, err: SendBirdError) => {
        if (err) {
          setStatus('error')
          const whitelist = ['Connection has been canceled.']
          if (!isInWhitelist(err.message, whitelist)) {
            logError(err.message, err)
          }
          return
        }
        const channelHandler = new sendBirdInstance.ChannelHandler()

        channelHandler.onMessageReceived = (__: any, message: UserMessage | AdminMessage) => {
          if (message.isUserMessage() || message.isAdminMessage()) {
            setLastUpdated(Date.now())
            updateUnreadMessageCount()
          }
        }
        sendBirdInstance.addChannelHandler(CHANNEL_HANDLER_ID, channelHandler)
        await getSBChannelList()
      }

      sendBirdInstance.connect(userId.toString(), accessToken, handleSBConnectCallback)
      setStatus('connecting')
    }

    return () => {
      setStatus('none')
      if (sendBirdInstance) {
        sendBirdInstance.removeAllChannelHandlers()
        sendBirdInstance.disconnect()
      }
    }
  }, [userId, accessToken, sendBirdInstance])

  return {
    channels,
    fetchLatestGroupChannelWithUnreadMessages,
    generateFallbackChannelName,
    isOwnMessage,
    lastUpdated,
    sendBird: sendBirdInstance,
    status,
    unreadMessageCount,
    updateUnreadMessageCount,
  }
}
